'use client';
import { CaptchaFormMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { sendContactMessage } from '@eliadmin/core/src/api/forms/forms-api';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@eliadmin/core/src/components/ui/form';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { CONTACT_CONFIG } from '@eliadmin/core/src/constants/contact-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { trackFormSubmitSuccess } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';

import { useForm } from 'react-hook-form';
import { z } from 'zod';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { GDPRTextContactForm } from '@eliadmin/core/src/utils/gdpr-tos/gdpr-text-contact-form';
import { Field } from '@eliadmin/core/src/components/form/field';
import { IoMdPaperPlane } from 'react-icons/io';
import { ButtonIcon } from '@eliadmin/core/src/components/ui/button-icon';

const contactSchema = (VM: FormValidationMessages) =>
  z.object({
    name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    phone: z.string().optional(),
    note: z.string(),
    gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
    'g-recaptcha-response': z.string().default(''),
  });

type ContactFormInputValues = z.input<ReturnType<typeof contactSchema>>;
export type ContactFormValues = z.output<ReturnType<typeof contactSchema>>;

export function ContactForm() {
  const { t } = useTranslations();
  const generalLayout = useGlobalStore(s => s.generalLayout);

  const { formValidationMessages } = useFormValidationMessages(t);

  const schema = contactSchema(formValidationMessages);

  const form = useForm<ContactFormInputValues, any, ContactFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      note: '',
      gdpr: false,
      'g-recaptcha-response': '',
    },
  });

  const { mutateAsync } = useMutation({
    mutationFn: sendContactMessage,
  });

  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    try {

      await mutateAsync({
        data: {
          ...data,

        },
        locale,
      });
      trackFormSubmitSuccess({
        form_id: 'contact',
        form_name: 'contact_form',
        form_destination: typeof window !== 'undefined' ? window.location.href : undefined,
        form_length: Object.keys(data).length,
        send_to: generalLayout.marketing?.gtm_id || '',
      });
      toast({
        title: t('text658'),
        variant: 'success',
      });
      router.push(ROUTES.HOME());
    } catch (err) {
      if (err instanceof ValidationError) {
        for (const [field, errors] of Object.entries(err.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text657'),
        description: (err as any)?.message,
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
        <CaptchaFormMessage errors={form.formState.errors} />
      <form
        onSubmit={handleSubmit}
        className={cn(
          'relative z-10 flex w-full max-w-[588px] flex-col items-center justify-center gap-6 rounded-[24px] bg-white p-4 sm:gap-8 sm:p-6 md:p-8 lg:gap-12 lg:p-12',
          'ea-reservation-form',
        )}
      >
        <div className="flex w-full flex-col items-center justify-center gap-4">
          <Field
            required
            name="name"
            label="Meno *"
            placeholder={t('text1044')}
            control={form.control}
            className={{
              wrapper: 'col-span-12 w-full',
            }}
          />
          <Field
            required
            name="email"
            type="email"
            label="E-mail *"
            placeholder="Vaša e-mailová adresa"
            control={form.control}
            className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
          />
          <Field
            name="phone"
            label={t('text1258')}
            placeholder="Vaše telefónne číslo +421..."
            control={form.control}
            className={{ wrapper: 'col-span-12 w-full sm:col-span-6' }}
          />
          <Field
            required
            name="note"
            label={t('text615')}
            placeholder="Krátko popíšte, ako vám môžeme pomôcť…"
            type="textarea"
            control={form.control}
            className={{ wrapper: 'col-span-12 w-full' }}
          />
          <FormField
            control={form.control}
            name="gdpr"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'col-span-12 flex items-start justify-start',
                  'ea-contact-form__gdpr-checkbox-item ea-contact-form__checkbox-item',
                )}
              >
                <FormControl>
                  <Checkbox
                    ref={field.ref}
                    name={field.name}
                    disabled={field.disabled}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className={cn('', 'ea-contact-form__gdpr-checkbox ea-contact-form__checkbox')}
                  />
                </FormControl>
                <FormLabel className={cn('pt-[3px]', 'ea-contact-form__gdpr-checkbox-label')}>
                  <GDPRTextContactForm locale={locale} />
                </FormLabel>
              </FormItem>
            )}
          />

          <Button
            type="submit"
            variant={CONTACT_CONFIG.form.submit.variant}
            className={cn('mt-2 w-full sm:w-fit', 'ea-contact-form__submit')}
            isLoading={form.formState.isSubmitting}
          >
            <IoMdPaperPlane className="h-5 w-5" />
            {t('text2343')}
          </Button>
          <div className="mt-2 flex items-center justify-center gap-[10px]">
            <ButtonIcon url="/images/icons/security-safe.svg" alt={t('text3767')} />
            <p className="text-sm font-normal leading-[100%] text-secondary">
              Bezpečné odoslanie · žiadny spam · odpoveď do 24 h
            </p>
          </div>
        </div>
      </form>
    </Form>
  );
}
